import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Tooltip,
  Avatar,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  ExpandLess,
  ExpandMore,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  BugReport as BugIcon,
  Assignment as TaskIcon,
  LibraryBooks as StoryIcon,
  ViewModule as EpicIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';

// Types
interface Task {
  id: number;
  title: string;
  description?: string;
  type: 'epic' | 'story' | 'task' | 'bug';
  status: 'todo' | 'in_progress' | 'done' | 'blocked' | 'backlog';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to?: number;
  assigned_user?: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  epic_lead?: {
    id: number;
    name: string;
    email: string;
  };
  parent_task_id?: number;
  estimated_hours?: number;
  story_points?: number;
  due_date?: string;
  acceptance_criteria?: string;
  progress_percentage?: number;
  child_tasks?: Task[];
  created_at: string;
  updated_at: string;
}

interface Project {
  id: number;
  name: string;
  description?: string;
  status: string;
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar?: string | null;
}

interface TaskFormData {
  title: string;
  description: string;
  type: 'epic' | 'story' | 'task' | 'bug';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to: string;
  parent_task_id: number | null | undefined;
  estimated_hours: string;
  story_points: string;
  due_date: string;
  acceptance_criteria: string;
}

const ProjectOverview: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth(); // Keep for potential future use
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEpics, setExpandedEpics] = useState<Set<number>>(new Set());
  const [expandedStories, setExpandedStories] = useState<Set<number>>(new Set());
  const [createTaskOpen, setCreateTaskOpen] = useState<boolean>(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskDetailsOpen, setTaskDetailsOpen] = useState<boolean>(false);
  const [assignableMembers, setAssignableMembers] = useState<TeamMember[]>([]);

  // Form state for task creation/editing
  const [taskForm, setTaskForm] = useState<TaskFormData>({
    title: '',
    description: '',
    type: 'task',
    priority: 'normal',
    assigned_to: '',
    parent_task_id: null,
    estimated_hours: '',
    story_points: '',
    due_date: '',
    acceptance_criteria: '',
  });

  useEffect(() => {
    fetchProjectData();
    fetchTasks();
  }, [projectId]);

  // Fetch assignable members when task dialog opens
  useEffect(() => {
    if (createTaskOpen || editingTask) {
      fetchAssignableMembers();
    }
  }, [createTaskOpen, editingTask]);

  const fetchProjectData = async (): Promise<void> => {
    try {
      const response = await ApiService.get<{data: Project}>(`/projects/${projectId}`);
      setProject(response.data);
    } catch (err) {
      setError('Failed to load project data');
      console.error(err);
    }
  };

  const fetchTasks = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await ApiService.get<{data: { tasks: Task[], project: Project }}>(`/projects/${projectId}/tasks`);
      const fetchedTasks = response.data.tasks || [];
      setTasks(fetchedTasks);
      
      // Auto-expand all epics by default
      const epicIds = new Set(
        fetchedTasks
          .filter(task => task.type === 'epic')
          .map(task => task.id)
      );
      setExpandedEpics(epicIds);
      
      // Auto-expand all stories that have child tasks
      const storyIds = new Set(
        fetchedTasks
          .filter(task => task.type === 'story' && task.child_tasks && task.child_tasks.length > 0)
          .map(task => task.id)
      );
      setExpandedStories(storyIds);
    } catch (err) {
      setError('Failed to load tasks');
      console.error(err);
      setTasks([]); // Ensure tasks is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableMembers = async (): Promise<void> => {
    try {
      const response = await ApiService.get<{
        data: { 
          team_members: TeamMember[],
          project: { id: number, name: string }
        }
      }>(`/projects/${projectId}/tasks/assignable-members`);
      setAssignableMembers(response.data.team_members || []);
    } catch (err) {
      console.error('Failed to load assignable members:', err);
      setAssignableMembers([]);
    }
  };

  const handleCreateTask = async (): Promise<void> => {
    try {
      const payload = {
        ...taskForm,
        estimated_hours: taskForm.estimated_hours ? parseInt(taskForm.estimated_hours) : null,
        story_points: taskForm.story_points ? parseInt(taskForm.story_points) : null,
        assigned_to: taskForm.assigned_to || null,
        due_date: taskForm.due_date || null,
      };

      await ApiService.post(`/projects/${projectId}/tasks`, payload);

      setCreateTaskOpen(false);
      resetTaskForm();
      fetchTasks();
    } catch (err) {
      console.error('Failed to create task:', err);
      setError('Failed to create task');
    }
  };

  const handleUpdateTask = async (): Promise<void> => {
    if (!editingTask) return;

    try {
      const payload = {
        ...taskForm,
        estimated_hours: taskForm.estimated_hours ? parseInt(taskForm.estimated_hours) : null,
        story_points: taskForm.story_points ? parseInt(taskForm.story_points) : null,
        assigned_to: taskForm.assigned_to || null,
        due_date: taskForm.due_date || null,
      };

      await ApiService.put(`/projects/${projectId}/tasks/${editingTask.id}`, payload);

      setEditingTask(null);
      resetTaskForm();
      fetchTasks();
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId: number): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await ApiService.delete(`/projects/${projectId}/tasks/${taskId}`);
        fetchTasks();
      } catch (err) {
        console.error('Failed to delete task:', err);
        setError('Failed to delete task');
      }
    }
  };

  const resetTaskForm = (): void => {
    setTaskForm({
      title: '',
      description: '',
      type: 'task',
      priority: 'normal',
      assigned_to: '',
      parent_task_id: null,
      estimated_hours: '',
      story_points: '',
      due_date: '',
      acceptance_criteria: '',
    });
  };

  const openEditDialog = (task: Task): void => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      type: task.type,
      priority: task.priority,
      assigned_to: task.assigned_to?.toString() || '',
      parent_task_id: task.parent_task_id,
      estimated_hours: task.estimated_hours?.toString() || '',
      story_points: task.story_points?.toString() || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      acceptance_criteria: task.acceptance_criteria || '',
    });
  };

  const toggleEpicExpansion = (epicId: number): void => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  const toggleStoryExpansion = (storyId: number): void => {
    const newExpanded = new Set(expandedStories);
    if (newExpanded.has(storyId)) {
      newExpanded.delete(storyId);
    } else {
      newExpanded.add(storyId);
    }
    setExpandedStories(newExpanded);
  };

  const getTaskIcon = (type: string): React.ReactElement => {
    switch (type) {
      case 'epic': return <EpicIcon />;
      case 'story': return <StoryIcon />;
      case 'task': return <TaskIcon />;
      case 'bug': return <BugIcon />;
      default: return <TaskIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'primary' | 'default' | 'error' => {
    switch (status) {
      case 'done': return 'success';
      case 'in_progress': return 'warning';
      case 'todo': return 'primary';
      case 'backlog': return 'default';
      case 'blocked': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'primary' | 'default' => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const formatDueDate = (dueDate: string): string | null => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const renderTaskCard = (task: Task, level: number = 0): React.ReactElement => {
    const isEpic = task.type === 'epic';
    const isStory = task.type === 'story';
    const hasChildren = task.child_tasks && task.child_tasks.length > 0;
    const isExpanded = isEpic ? expandedEpics.has(task.id) : expandedStories.has(task.id);

    return (
      <Card 
        key={task.id} 
        sx={{ 
          mb: 1, 
          ml: level * 2,
          border: isEpic ? '2px solid #1976d2' : isStory ? '1px solid #2196f3' : '1px solid #e0e0e0',
          backgroundColor: isEpic ? '#f3f8ff' : isStory ? '#f8fbff' : 'white',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 3,
          },
        }}
        onClick={() => {
          setSelectedTask(task);
          setTaskDetailsOpen(true);
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" flex={1}>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    isEpic ? toggleEpicExpansion(task.id) : toggleStoryExpansion(task.id);
                  }}
                  sx={{ mr: 1 }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
              
              <Box sx={{ mr: 1 }}>
                {getTaskIcon(task.type)}
              </Box>

              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant={isEpic ? 'h6' : isStory ? 'subtitle1' : 'body1'} component="div">
                    {task.title}
                  </Typography>
                  
                  {isEpic && hasChildren && (
                    <Chip
                      size="small"
                      label={`${task.child_tasks?.length || 0} stories`}
                      sx={{ ml: 1 }}
                      color="info"
                      variant="outlined"
                    />
                  )}
                  
                  {isEpic && task.epic_lead && (
                    <Chip
                      size="small"
                      label={`Lead: ${task.epic_lead.name}`}
                      sx={{ ml: 1 }}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                
                {task.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {task.description}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={task.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(task.status)}
                    variant="outlined"
                  />
                  
                  <Chip
                    size="small"
                    label={task.priority.toUpperCase()}
                    color={getPriorityColor(task.priority)}
                    variant="outlined"
                  />

                  {task.assigned_user && (
                    <Tooltip title={`Assigned to ${task.assigned_user.name}`}>
                      <Avatar
                        sx={{ width: 24, height: 24 }}
                        src={task.assigned_user.avatar}
                      >
                        {task.assigned_user.name.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  )}

                  {task.estimated_hours && (
                    <Chip
                      size="small"
                      icon={<TimerIcon />}
                      label={`${task.estimated_hours}h`}
                      variant="outlined"
                    />
                  )}

                  {task.story_points && (
                    <Chip
                      size="small"
                      label={`${task.story_points} pts`}
                      variant="outlined"
                    />
                  )}

                  {task.due_date && (
                    <Chip
                      size="small"
                      icon={<CalendarIcon />}
                      label={formatDueDate(task.due_date)}
                      variant="outlined"
                      color={new Date(task.due_date) < new Date() ? 'error' : 'default'}
                    />
                  )}
                </Box>

                {task.progress_percentage !== undefined && (
                  <Box sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption">Progress</Typography>
                      <Typography variant="caption">{task.progress_percentage}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={task.progress_percentage}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
              </Box>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setSelectedTask(task);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Box>

          {hasChildren && isExpanded && (
            <Box sx={{ mt: 2 }}>
              {task.child_tasks?.map(childTask => renderTaskCard(childTask, level + 1))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading tasks...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Organize tasks by hierarchy
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const epics = safeTasks.filter(task => task.type === 'epic');
  const standaloneItems = safeTasks.filter(task => !task.parent_task_id && task.type !== 'epic');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {project?.name || 'Project Overview'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your project tasks, epics, and stories
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateTaskOpen(true)}
        >
          Add Task
        </Button>
      </Box>

      {/* Task Hierarchy */}
      <Box>
        {/* Epics */}
        {epics.map(epic => renderTaskCard(epic))}

        {/* Standalone Tasks and Bugs */}
        {standaloneItems.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Other Tasks
            </Typography>
            {standaloneItems.map(task => renderTaskCard(task))}
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          if (selectedTask) {
            openEditDialog(selectedTask);
          }
          setAnchorEl(null);
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedTask) {
            setTaskForm(prev => ({ ...prev, parent_task_id: selectedTask.id, type: 'story' }));
            setCreateTaskOpen(true);
          }
          setAnchorEl(null);
        }}>
          Add Sub-task
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedTask) {
              handleDeleteTask(selectedTask.id);
            }
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Task Dialog */}
      <Dialog
        open={createTaskOpen || editingTask !== null}
        onClose={() => {
          setCreateTaskOpen(false);
          setEditingTask(null);
          resetTaskForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={taskForm.title}
              onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={taskForm.description}
              onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={taskForm.type}
                  onChange={(e: SelectChangeEvent) => setTaskForm(prev => ({ ...prev, type: e.target.value as 'epic' | 'story' | 'task' | 'bug' }))}
                  label="Type"
                >
                  <MenuItem value="epic">Epic</MenuItem>
                  <MenuItem value="story">Story</MenuItem>
                  <MenuItem value="task">Task</MenuItem>
                  <MenuItem value="bug">Bug</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={taskForm.priority}
                  onChange={(e: SelectChangeEvent) => setTaskForm(prev => ({ ...prev, priority: e.target.value as 'low' | 'normal' | 'high' | 'urgent' }))}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Assignment Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={taskForm.assigned_to}
                onChange={(e: SelectChangeEvent) => setTaskForm(prev => ({ ...prev, assigned_to: e.target.value }))}
                label="Assign To"
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {assignableMembers.map(member => (
                  <MenuItem key={member.id} value={member.id.toString()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                        src={member.avatar || undefined}
                      >
                        {member.name.charAt(0)}
                      </Avatar>
                      <span>{member.name}</span>
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({member.role || 'Team Member'})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Estimated Hours"
                type="number"
                value={taskForm.estimated_hours}
                onChange={(e) => setTaskForm(prev => ({ ...prev, estimated_hours: e.target.value }))}
              />

              <TextField
                fullWidth
                label="Story Points"
                type="number"
                value={taskForm.story_points}
                onChange={(e) => setTaskForm(prev => ({ ...prev, story_points: e.target.value }))}
              />
            </Box>

            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={taskForm.due_date}
              onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {taskForm.type === 'story' && (
              <TextField
                fullWidth
                label="Acceptance Criteria"
                multiline
                rows={4}
                value={taskForm.acceptance_criteria}
                onChange={(e) => setTaskForm(prev => ({ ...prev, acceptance_criteria: e.target.value }))}
                placeholder="- Criterion 1&#10;- Criterion 2&#10;- Criterion 3"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCreateTaskOpen(false);
              setEditingTask(null);
              resetTaskForm();
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={editingTask ? handleUpdateTask : handleCreateTask}
            variant="contained"
          >
            {editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Details Dialog */}
      <Dialog 
        open={taskDetailsOpen} 
        onClose={() => setTaskDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedTask && getTaskIcon(selectedTask.type)}
            <Typography variant="h6">Task Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Title */}
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="h6">
                  {selectedTask.title}
                </Typography>
              </Box>

              {/* Type, Status, Priority */}
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Type: ${selectedTask.type.toUpperCase()}`}
                  color="primary"
                />
                <Chip
                  size="small"
                  label={`Status: ${selectedTask.status.replace('_', ' ').toUpperCase()}`}
                  color={getStatusColor(selectedTask.status)}
                />
                <Chip
                  size="small"
                  label={`Priority: ${selectedTask.priority.toUpperCase()}`}
                  color={getPriorityColor(selectedTask.priority)}
                />
              </Box>

              {/* Description */}
              {selectedTask.description && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {selectedTask.description}
                  </Typography>
                </Box>
              )}

              {/* Assigned User */}
              {selectedTask.assigned_user && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Avatar
                      sx={{ width: 32, height: 32 }}
                      src={selectedTask.assigned_user.avatar}
                    >
                      {selectedTask.assigned_user.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {selectedTask.assigned_user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedTask.assigned_user.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Epic Lead */}
              {selectedTask.epic_lead && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Epic Lead
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {selectedTask.epic_lead.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {selectedTask.epic_lead.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedTask.epic_lead.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Estimation */}
              <Box display="flex" gap={2}>
                {selectedTask.estimated_hours && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Estimated Hours
                    </Typography>
                    <Typography variant="body1">
                      <TimerIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {selectedTask.estimated_hours}h
                    </Typography>
                  </Box>
                )}
                {selectedTask.story_points && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Story Points
                    </Typography>
                    <Typography variant="body1">
                      {selectedTask.story_points} pts
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Due Date */}
              {selectedTask.due_date && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    <CalendarIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    {formatDueDate(selectedTask.due_date)}
                  </Typography>
                </Box>
              )}

              {/* Progress */}
              {selectedTask.progress_percentage !== undefined && (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="overline" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2">{selectedTask.progress_percentage}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={selectedTask.progress_percentage}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )}

              {/* Acceptance Criteria */}
              {selectedTask.acceptance_criteria && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Acceptance Criteria
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="div"
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      mt: 0.5,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    {selectedTask.acceptance_criteria}
                  </Typography>
                </Box>
              )}

              {/* Timestamps */}
              <Box display="flex" gap={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedTask.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Updated
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedTask.updated_at).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Task ID */}
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Task ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  #{selectedTask.id}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDetailsOpen(false)}>
            Close
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              setTaskDetailsOpen(false);
              setEditingTask(selectedTask);
              if (selectedTask) {
                setTaskForm({
                  title: selectedTask.title,
                  description: selectedTask.description || '',
                  type: selectedTask.type,
                  priority: selectedTask.priority,
                  assigned_to: selectedTask.assigned_to?.toString() || '',
                  parent_task_id: selectedTask.parent_task_id,
                  estimated_hours: selectedTask.estimated_hours?.toString() || '',
                  story_points: selectedTask.story_points?.toString() || '',
                  due_date: selectedTask.due_date || '',
                  acceptance_criteria: selectedTask.acceptance_criteria || '',
                });
                setCreateTaskOpen(true);
              }
            }}
          >
            Edit Task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectOverview;